import { Controller } from "@hotwired/stimulus"
import { showToast } from "../utils/show_toast"

const MIN_CROP_PX = 80
const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"]

export default class extends Controller {
  static targets = ["imageWrapper", "image", "overlay", "loader", "confirm", "cancel"]
  static values = {
    outputWidth: { type: Number, default: 0 },
    outputHeight: { type: Number, default: 0 },
    aspectRatio: { type: Number, default: 4 / 3 }
  }

  connect() {
    this.boundOpen = this.open.bind(this)
    this.boundMouseMove = this.onMouseMove.bind(this)
    this.boundMouseUp = this.onMouseUp.bind(this)
    this.boundConfirm = this.onConfirmClick.bind(this)
    this.boundCancel = this.onCancelClick.bind(this)
    this.boundBackdrop = this.onBackdropClick.bind(this)

    this.element.addEventListener("image-crop:open", this.boundOpen)
    this.confirmTarget.addEventListener("click", this.boundConfirm)
    this.cancelTargets.forEach((el) => el.addEventListener("click", this.boundCancel))
    this.element.addEventListener("click", this.boundBackdrop)
  }

  disconnect() {
    this.element.removeEventListener("image-crop:open", this.boundOpen)
    this.confirmTarget.removeEventListener("click", this.boundConfirm)
    this.cancelTargets.forEach((el) => el.removeEventListener("click", this.boundCancel))
    this.element.removeEventListener("click", this.boundBackdrop)
    document.removeEventListener("mousemove", this.boundMouseMove)
    document.removeEventListener("mouseup", this.boundMouseUp)
    this.teardown()
  }

  onConfirmClick() {
    this.confirm()
  }

  onCancelClick() {
    this.cancel()
  }

  onBackdropClick(e) {
    if (e.target === this.element) this.cancel()
  }

  open(event) {
    const file = event.detail?.file
    if (!file) return
    this.cropFile = file
    this.element.classList.remove("hidden")
    this.loaderTarget.classList.remove("hidden")
    this.imageWrapperTarget.classList.add("hidden")

    const img = this.imageTarget
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)

      // Size the image to fit the available viewport space
      // 80px = horizontal modal padding, 174px = header + footer + vertical padding
      const maxW = Math.min(window.innerWidth - 80, 1280) - 32
      const maxH = window.innerHeight * 0.95 - 174
      const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1)
      img.style.width = Math.round(img.naturalWidth * scale) + "px"
      img.style.height = Math.round(img.naturalHeight * scale) + "px"

      this.loaderTarget.classList.add("hidden")
      this.imageWrapperTarget.classList.remove("hidden")

      this.initCrop()
      document.addEventListener("mousemove", this.boundMouseMove)
      document.addEventListener("mouseup", this.boundMouseUp)
    }
    img.src = url
  }

  initCrop() {
    const img = this.imageTarget
    const iw = img.offsetWidth
    const ih = img.offsetHeight
    this.imgBounds = { w: iw, h: ih }

    // Fit the largest possible 4:3 crop inside the image
    let cropW = iw
    let cropH = cropW / this.aspectRatioValue
    if (cropH > ih) {
      cropH = ih
      cropW = cropH * this.aspectRatioValue
    }

    this.crop = {
      x: (iw - cropW) / 2,
      y: (ih - cropH) / 2,
      w: cropW,
      h: cropH,
    }

    this.renderOverlay()
    this.bindOverlayMove()
    this.buildHandles()
  }

  renderOverlay() {
    const overlay = this.overlayTarget
    const { x, y, w, h } = this.crop
    overlay.style.left = x + "px"
    overlay.style.top = y + "px"
    overlay.style.width = w + "px"
    overlay.style.height = h + "px"
    overlay.classList.remove("hidden")
  }

  bindOverlayMove() {
    this.boundOverlayMouseDown = (e) => {
      if (e.target.dataset.handle) return
      e.preventDefault()
      this.drag = { type: "move", startX: e.clientX, startY: e.clientY, startCrop: { ...this.crop } }
      this.startDragCapture()
    }
    this.overlayTarget.addEventListener("mousedown", this.boundOverlayMouseDown)
  }

  buildHandles() {
    const overlay = this.overlayTarget
    overlay.querySelectorAll(".crop-handle").forEach((el) => el.remove())

    HANDLES.forEach((id) => {
      const el = document.createElement("div")
      el.className = `crop-handle crop-handle-${id}`
      el.dataset.handle = id
      el.addEventListener("mousedown", (e) => {
        e.stopPropagation()
        e.preventDefault()
        this.drag = { type: "resize", handle: id, startX: e.clientX, startY: e.clientY, startCrop: { ...this.crop } }
        this.startDragCapture()
      })
      overlay.appendChild(el)
    })
  }

  // Transparent full-screen layer that absorbs all pointer events during a drag.
  // Prevents any element underneath (buttons, backdrop) from receiving clicks
  // when the mouse is released at the end of a drag.
  startDragCapture() {
    if (this.dragCapture) return
    this.dragCapture = document.createElement("div")
    this.dragCapture.style.cssText = "position:fixed;inset:0;z-index:9999;cursor:grabbing;"
    document.body.appendChild(this.dragCapture)
  }

  stopDragCapture() {
    this.dragCapture?.remove()
    this.dragCapture = null
  }

  onMouseMove(e) {
    if (!this.drag) return
    // If no mouse button is held (released outside the window), end drag silently
    if (e.buttons === 0) {
      this.drag = null
      return
    }

    const dx = e.clientX - this.drag.startX
    const dy = e.clientY - this.drag.startY

    if (this.drag.type === "move") {
      this.applyMove(this.drag.startCrop, dx, dy)
    } else {
      this.applyResize(this.drag.handle, this.drag.startCrop, dx, dy)
    }

    this.renderOverlay()
  }

  onMouseUp() {
    this.drag = null
    this.stopDragCapture()
  }

  applyMove(startCrop, dx, dy) {
    const { w: iw, h: ih } = this.imgBounds
    const { w, h } = startCrop
    this.crop = {
      x: Math.max(0, Math.min(iw - w, startCrop.x + dx)),
      y: Math.max(0, Math.min(ih - h, startCrop.y + dy)),
      w,
      h,
    }
  }

  applyResize(handleId, start, dx, dy) {
    const { w: iw, h: ih } = this.imgBounds
    let { x, y, w, h } = start

    switch (handleId) {
      case "se": w = start.w + dx;            h = w / this.aspectRatioValue; break
      case "sw": w = start.w - dx;            h = w / this.aspectRatioValue; x = start.x + start.w - w; break
      case "ne": w = start.w + dx;            h = w / this.aspectRatioValue; y = start.y + start.h - h; break
      case "nw": w = start.w - dx;            h = w / this.aspectRatioValue; x = start.x + start.w - w; y = start.y + start.h - h; break
      case "e":  w = start.w + dx;            h = w / this.aspectRatioValue; y = start.y + (start.h - h) / 2; break
      case "w":  w = start.w - dx;            h = w / this.aspectRatioValue; x = start.x + start.w - w; y = start.y + (start.h - h) / 2; break
      case "s":  h = start.h + dy;            w = h * this.aspectRatioValue; x = start.x + (start.w - w) / 2; break
      case "n":  h = start.h - dy;            w = h * this.aspectRatioValue; x = start.x + (start.w - w) / 2; y = start.y + start.h - h; break
    }

    // Clamp to image bounds, preserving 4:3
    if (x < 0)      { x = 0;  w = start.x + start.w; h = w / this.aspectRatioValue }
    if (y < 0)      { y = 0;  h = start.y + start.h; w = h * this.aspectRatioValue }
    if (x + w > iw) { w = iw - x;                    h = w / this.aspectRatioValue }
    if (y + h > ih) { h = ih - y;                    w = h * this.aspectRatioValue }

    if (w < MIN_CROP_PX) return
    this.crop = { x, y, w, h }
  }

  confirm() {
    if (!this.cropFile || !this.crop) return

    const img = this.imageTarget
    const scaleX = img.naturalWidth / img.offsetWidth
    const scaleY = img.naturalHeight / img.offsetHeight
    const srcX = this.crop.x * scaleX
    const srcY = this.crop.y * scaleY
    const srcW = this.crop.w * scaleX
    const srcH = this.crop.h * scaleY

    const out = document.createElement("canvas")
    const outputWidth = this.outputWidthValue > 0 ? this.outputWidthValue : Math.round(srcW)
    const outputHeight = this.outputHeightValue > 0 ? this.outputHeightValue : Math.round(srcH)
    out.width = outputWidth
    out.height = outputHeight
    const ctx = out.getContext("2d")
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputWidth, outputHeight)

    const stem = this.cropFile.name.replace(/\.[^.]+$/i, "")

    // Lossless PNG of the selected crop. Server-side ImageUploads::Processor does the WebP encode.
    out.toBlob(
      (blob) => {
        if (!blob) {
          showToast("Could not export cropped image", "error")
          return
        }

        this.dispatchCroppedFile(blob, stem)
      },
      "image/png"
    )
  }

  dispatchCroppedFile(blob, stem) {
    const croppedFile = new File([blob], `${stem}-cropped.png`, { type: "image/png" })

    // Dispatch before close so the element is still active for event bubbling
    this.element.dispatchEvent(new CustomEvent("image-crop:complete", { detail: { file: croppedFile }, bubbles: true }))
    this.close()
  }

  cancel() {
    this.close()
  }

  close() {
    this.teardown()
    this.element.classList.add("hidden")
  }

  teardown() {
    document.removeEventListener("mousemove", this.boundMouseMove)
    document.removeEventListener("mouseup", this.boundMouseUp)
    this.stopDragCapture()
    this.drag = null
    this.crop = null
    this.cropFile = null
    this.imgBounds = null

    this.loaderTarget.classList.remove("hidden")
    this.imageWrapperTarget.classList.add("hidden")

    const overlay = this.overlayTarget
    overlay.classList.add("hidden")
    overlay.querySelectorAll(".crop-handle").forEach((el) => el.remove())
    if (this.boundOverlayMouseDown) {
      overlay.removeEventListener("mousedown", this.boundOverlayMouseDown)
      this.boundOverlayMouseDown = null
    }

    const img = this.imageTarget
    img.src = ""
    img.style.width = ""
    img.style.height = ""
  }
}
